import { io, Socket } from 'socket.io-client';
import { API_BASE_URL, STORAGE_KEYS } from '../constants';

// Socket.IO connects to the server root — strip the /v1 API path prefix
const SOCKET_URL = API_BASE_URL.replace(/\/v1\/?$/, '');

export const BATCH_EVENTS = {
  CREATED: 'batch:created',
  UPDATED: 'batch:updated',
  DELETED: 'batch:deleted',
  DONE: 'batch:done',
} as const;

export const QUEUE_EVENTS = {
  CREATED: 'queue:created',
  DELETED: 'queue:deleted',
  REORDERED: 'queue:reordered',
} as const;

export interface BatchCreatedPayload {
  id: string;
  ovenId: string;
  productId?: string;
  productName?: string;
  productFamily?: string;
  protein?: string;
  allergens?: string;
  wipNumber?: string;
  lotNumber?: string;
  recipeName?: string;
  recipeId?: string;
  customerName?: string;
  startTime: string;
  endTime: string;
  status: 'scheduled' | 'in-progress' | 'completed' | 'cancelled';
  notes?: string;
  color?: string;
  assignmentType?: 'product' | 'oven-down' | 'oven-washing';
  moistureReadings?: { id: string; value: number; recordedAt: string }[];
}

export type BatchUpdatedPayload = BatchCreatedPayload;

export interface BatchDeletedPayload {
  id: string;
}

export interface BatchDonePayload {
  batchId: string;
  ovenName: string;
  smsSent: boolean;
}

export interface QueueCreatedPayload {
  id: string;
  productId: string;
  productName: string;
  wipNumber: string;
  lotNumber: string;
  displayOrder: number;
}

export interface QueueDeletedPayload {
  id: string;
}

export interface QueueReorderedPayload {
  items: Array<{ id: string; displayOrder: number }>;
}

let socket: Socket | null = null;

/**
 * Connect to the Socket.IO server with JWT authentication.
 */
export const connectSocket = (): Socket => {
  if (socket) {
    return socket;
  }

  socket = io(SOCKET_URL, {
    auth: (cb) => cb({ token: localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN) }),
    transports: ['websocket', 'polling'],
    reconnection: true,
    reconnectionAttempts: Infinity,
    reconnectionDelay: 1000,
    reconnectionDelayMax: 5000,
  });

  socket.on('connect', () => {
    console.log('Socket connected:', socket?.id);
  });

  socket.on('connect_error', (err) => {
    console.error('Socket connection error:', err.message);
  });

  socket.on('disconnect', (reason) => {
    console.log('Socket disconnected:', reason);
  });

  return socket;
};

/**
 * Disconnect the socket. Call on logout.
 */
export const disconnectSocket = (): void => {
  if (socket) {
    socket.disconnect();
    socket = null;
  }
};

/**
 * Get the current socket instance.
 */
export const getSocket = (): Socket | null => socket;
